import { ArrowRight } from "lucide-react";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AdherenceBar } from "../../adherence/AdherenceBar";
import { PreliminaryBadge } from "../../adherence/PreliminaryBadge";
import { Section, type DiagnosticTabProps } from "../../DiagnosticSection";
import { companiesTile, environmentOverview } from "../../diagnosticData";
import { adherenceIndex } from "../../data/adherenceIndex";
import { developmentsAnalyzed } from "../../data/wricefDecisions";
import { scopeItemsTile } from "../../data/scopeItems";
import {
  scopeBasisLabel,
  scopeItemsHint,
  scopeItemsUnavailableReason,
} from "../../scopeItems/scopeItemsText";
import { NO_VALUE } from "../../sectionState";
import { useAreaAvailable } from "../../projectAreas";

/* -------------------------------------------------------------------------- */
/* 1 · visão geral do ambiente (#873)                                          */
/* -------------------------------------------------------------------------- */

/**
 * O card de abertura do Ato 1, nos indicadores do desenho aprovado, com o tile
 * "Empresas" da EF-DIAG-002 na 2ª posição e "Scope items ativos" da EF-FUNC-004
 * na 4ª, onde antes ficava "Módulos em uso".
 *
 * Duas células não copiam o desenho ao pé da letra, e as duas por falta de
 * dado que o sustente, não por escolha de estilo:
 *
 * - **Usuários ativos** não diz "últimos 90 dias". A janela vem da run e o
 *   default é 36 meses; escrever 90 dias inventaria o recorte do número.
 * - **Interfaces** não diz "214 ativas de 428". Destination RFC não tem sinal
 *   de tráfego nenhum, só o IDoc tem `record_count`. O denominador é a parte
 *   medível, e a célula declara isso em vez de afirmar atividade sobre linhas
 *   das quais não se sabe nada.
 */
export function EnvironmentOverviewCard({ sections, snapshots, validation }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const overview = environmentOverview(sections, formatCount);
  const hasAdherence = useAreaAvailable("adherence");
  const hasCode = useAreaAvailable("architect");

  return (
    <Section
      sections={sections}
      name="executive_summary"
      title={t("Environment at a glance")}
      snapshots={snapshots}
      className="border-l-4 border-l-[#ffc83d]"
    >
      <div
        data-testid="environment-overview"
        className="grid grid-cols-2 gap-x-6 gap-y-5 md:grid-cols-3 lg:grid-cols-5"
      >
        <OverviewCell
          testId="overview-active-users"
          label={t("Active users")}
          value={overview.activeUsers.text}
          to="active-users"
          actionLabel={t("See users")}
        />
        <CompaniesCell validation={validation} />
        {hasAdherence && <ScopeItemsCell validation={validation} />}
        {hasCode && (
          <OverviewCell
            testId="overview-executable-developments"
            label={t("Executable developments")}
            value={executableDevelopments(sections, validation)}
            hint={t("Z/Y objects in the WRICEF catalog")}
          />
        )}
        {hasAdherence && <AdherenceCell validation={validation} />}
      </div>
    </Section>
  );
}

/**
 * O tile "Aderência ao padrão" (EF-FUNC-011, RF-06 e RF-09), no lugar do que
 * contava Ativar sobre o escopo aprovado e dava 100% comparando o escopo com ele
 * mesmo.
 *
 * Tudo vem do índice calculado pelo backend: o % de Padrão do consolidado, a
 * barra das três cores, a cobertura da solução (padrão + extensão) e os GAPs do
 * Registro inteiro, que não são o número de vermelhos. A célula abre o
 * drill-down de três abas.
 *
 * Com item sem disposição, o índice é preliminar e o tile diz quantos faltam.
 * Sem base (tudo sem disposição), o número é o travessão: 0% afirmaria que nada
 * é padrão.
 */
function AdherenceCell({ validation }: Pick<DiagnosticTabProps, "validation">) {
  const { t } = useTranslation();
  const state = adherenceIndex(validation);
  const common = {
    testId: "overview-standard-fit",
    label: t("Standard fit"),
    to: "adherence",
    actionLabel: t("See adherence"),
  };

  if (state.kind !== "counted") {
    return (
      <OverviewCell
        {...common}
        value={NO_VALUE}
        hint={
          state.kind === "empty"
            ? t("No scope item, WRICEF or GAP assessed yet.")
            : t("The adherence index could not be read right now.")
        }
      />
    );
  }

  const { index } = state;
  const total = index.rows.consolidated;
  const gaps = tp(
    t,
    index.gaps.total,
    "{{count}} GAP ({{essential}} essential)",
    "{{count}} GAPs ({{essential}} essential)",
    { essential: formatCount(index.gaps.essential) },
  );
  return (
    <OverviewCell
      {...common}
      labelExtra={index.preliminary && <PreliminaryBadge />}
      value={total.standard.pct == null ? NO_VALUE : `${total.standard.pct}%`}
      hint={
        total.coverage_pct == null
          ? gaps
          : `${t("Solution coverage {{pct}}%", { pct: total.coverage_pct })} · ${gaps}`
      }
    >
      {total.base > 0 && (
        <div className="mt-2">
          <AdherenceBar row={total} height="h-1.5" />
        </div>
      )}
      {index.preliminary && (
        <div className="mt-1 text-[11px] leading-snug text-[#c77700]">
          {tp(
            t,
            index.undecided_count,
            "{{count}} item with no disposition",
            "{{count}} items with no disposition",
          )}
        </div>
      )}
    </OverviewCell>
  );
}

/**
 * O tile "Empresas": as company codes do cliente, sem as empresas-modelo da SAP
 * (EF-DIAG-002, #1342). A célula inteira abre a estrutura organizacional.
 *
 * Quando o número não é conhecido, o valor é o travessão, como nas vizinhas, e a
 * legenda diz em português o porquê. O link fica em todos os casos: a tela de
 * detalhe explica com mais calma o que falta.
 */
function CompaniesCell({ validation }: Pick<DiagnosticTabProps, "validation">) {
  const { t } = useTranslation();
  const tile = companiesTile(validation);

  const hint =
    tile.kind === "classified"
      ? t("client company codes · {{inScope}} in scope", { inScope: formatCount(tile.inScope) })
      : tile.kind === "unclassified"
        ? t("{{read}} found in SAP · the client's still need to be separated", {
            read: formatCount(tile.read),
          })
        : tile.kind === "empty"
          ? t("The SAP read did not bring the companies")
          : tile.kind === "failed"
            ? t("The companies could not be read right now")
            : t("It appears after the next Functional analysis.");

  return (
    <OverviewCell
      testId="overview-companies"
      label={t("Companies")}
      value={tile.kind === "classified" ? formatCount(tile.client) : NO_VALUE}
      hint={hint}
      to="org-structure"
      actionLabel={t("See organizational structure")}
    />
  );
}

/**
 * O tile "Scope items ativos" (EF-FUNC-004, #1344). A célula inteira abre o
 * detalhe somente leitura dos scope items.
 *
 * O número é o de ativos na base que o backend escolheu, e a célula sempre diz
 * qual é: o escopo aprovado, quando existe, ou a curadoria salva antes disso. O
 * resto do Diagnóstico (Ato 2, BOM do Ato 5) lê só o aprovado, então um número
 * sem base seria confundido com ele.
 *
 * O nome do tile segue a recomendação da pergunta 3 da issue, ainda sem
 * resposta do Marco.
 */
function ScopeItemsCell({ validation }: Pick<DiagnosticTabProps, "validation">) {
  const { t } = useTranslation();
  const tile = scopeItemsTile(validation);

  if (tile.kind !== "counted") {
    return (
      <OverviewCell
        testId="overview-scope-items"
        label={t("Active scope items")}
        value={NO_VALUE}
        hint={scopeItemsUnavailableReason(tile, t)}
        to="scope-items"
        actionLabel={t("See scope items")}
      />
    );
  }

  return (
    <OverviewCell
      testId="overview-scope-items"
      label={t("Active scope items")}
      value={formatCount(tile.active)}
      hint={scopeItemsHint(tile, t)}
      to="scope-items"
      actionLabel={t("See scope items")}
    >
      <div className="mt-1 text-[11px] leading-snug text-[#7b8799]">
        {scopeBasisLabel(tile, t)}
      </div>
    </OverviewCell>
  );
}

/**
 * Uma célula da visão geral do Ato 1.
 *
 * Com `to`, a célula inteira vira link para uma tela de detalhe (#1339): é ela
 * que o apresentador aponta na reunião, e não um "ver mais" no canto. O `to` é o
 * slug do detalhe, relativo ao Diagnóstico, porque os atos não recebem `projectId`.
 * Sem `to`, a célula é a mesma de antes. `labelExtra` vai na linha do rótulo,
 * como o selo "Preliminar" da aderência.
 */
export function OverviewCell({
  label,
  labelExtra,
  value,
  hint,
  testId,
  to,
  actionLabel,
  children,
}: {
  label: string;
  labelExtra?: ReactNode;
  value: string;
  hint?: string;
  testId?: string;
  to?: string;
  actionLabel?: string;
  children?: ReactNode;
}) {
  const body = (
    <>
      <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
        {label}
        {labelExtra}
      </div>
      <div className="mt-1 text-[22px] font-semibold leading-none text-[#08254c]">{value}</div>
      {hint && <div className="mt-1 text-[11px] leading-snug text-[#7b8799]">{hint}</div>}
      {children}
    </>
  );

  if (!to) return <div data-testid={testId}>{body}</div>;

  // O `-m-2 p-2` dá ao hover e ao anel de foco um respiro sem tirar a célula do
  // alinhamento das vizinhas, que continuam sem moldura.
  return (
    <Link
      to={to}
      data-testid={testId}
      className="-m-2 block rounded-md p-2 transition-colors hover:bg-[#f5f8fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c] focus-visible:ring-offset-2"
    >
      {body}
      {actionLabel && (
        <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#08254c]">
          {actionLabel}
          <ArrowRight aria-hidden className="h-3 w-3" />
        </div>
      )}
    </Link>
  );
}

/**
 * Desenvolvimentos executáveis (#1476): os objetos Z/Y com letra WRICEF, o mesmo
 * número do card "Código customizado por tipo".
 *
 * A caixa ocupa o lugar de "Interfaces", que contava destinos RFC. Na AL5 os dois
 * davam 59, e na reunião de 19/10/2026 a coincidência foi lida como o mesmo dado.
 * Com a decisão do arquiteto, o número é o universo dela; sem decisão, a soma das
 * letras do catálogo, que é o que o card desenha nos dois casos.
 */
function executableDevelopments(
  sections: DiagnosticTabProps["sections"],
  validation: DiagnosticTabProps["validation"],
): string {
  const count = developmentsAnalyzed(sections, validation);
  return count === undefined ? NO_VALUE : formatCount(count);
}
